// An ABC IMPORT LINK: `flatfive.app/?abc=<address>` opens the ABC file at that
// address, so anyone publishing ABC can link straight to a band (the way
// RealSheet's `?abc=` links open a tunebook; docs/competition-realsheet.md).
//
// - One tune opens on the sheet (the Score studio when the link names it).
// - A tunebook becomes the loaded list, exactly as Import does, and lands on
//   the home page's search listing it; `&tune=<title>` (optionally `&by=<composer>`)
//   opens that tune from it instead.
// - `&read=` and a recipe (tuneRecipe.ts) apply to the tune the link opens,
//   as they do on a `?tune=` link.
//
// The link is an instruction, not an identity: it is read once and removed
// from the address bar, which then reflects the tune like any other. A tune
// from a link is a tune from an import — the same as one pasted into Import.
//
// The fetch runs in the reader's browser, so the file's host must allow other
// sites to read it (CORS): GitHub's raw files do, most web pages do not. The
// request carries no cookies, gives up after 20 seconds and stops reading past
// 8 MB, and the text is read as ABC data, never run or rendered as markup.

import { isAbcSource, withAbcBookName } from '@/music/abc/book';
import type { IRealIndex, PlaylistEntry } from '@/music/ireal/parse';
import { isScoreEdition } from '@/music/score/edition';
import { isAbcScheme, parseTuneIndex } from '@/music/tuneIndex';
import { APP_ROUTES } from '@/navigation';
import { useStore } from '@/state/store';
import { RECIPE_KEYS, applyTuneRecipe, hasRecipe, parseTuneRecipe } from '@/components/tuneRecipe';

/** The query parameter that carries the address. */
export const ABC_LINK_PARAM = 'abc';
export const ABC_LINK_MAX_BYTES = 8 * 1024 * 1024;
export const ABC_LINK_TIMEOUT_MS = 20_000;

/** Where a tunebook lands: the home page's search, showing the loaded ABC list. */
export const ABC_BOOK_LIST = `${APP_ROUTES.home}?version=abc`;

/** Everything a link may carry, removed once it has been read. */
const LINK_KEYS = [ABC_LINK_PARAM, 'tune', 'by', 'read', ...RECIPE_KEYS];

export type AbcAddress = { ok: true; url: string } | { ok: false; error: string };

const NOT_AN_ADDRESS = "That ABC link isn't a web address.";

/**
 * The address a link's `abc=` value names, ready to fetch. Accepts https, http
 * (upgraded to https on an https page, where the browser would block it), a
 * `data:` URL carrying the ABC itself, and a bare host ("example.com/x.abc").
 * A GitHub file page becomes its raw file, since the page is HTML and GitHub
 * only lets other sites read the raw file.
 */
export function abcLinkAddress(raw: string, page: { protocol: string }): AbcAddress {
  const value = raw.trim();
  if (!value) return { ok: false, error: 'The ABC link has no address.' };
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    if (!/^[a-z0-9-]+(?:\.[a-z0-9-]+)+(?::\d+)?(?:[/?#]|$)/i.test(value)) {
      return { ok: false, error: NOT_AN_ADDRESS };
    }
    try {
      url = new URL(`https://${value}`);
    } catch {
      return { ok: false, error: NOT_AN_ADDRESS };
    }
  }
  if (url.protocol === 'data:') return { ok: true, url: url.href };
  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    return { ok: false, error: NOT_AN_ADDRESS };
  }
  if (url.protocol === 'http:' && page.protocol === 'https:') url.protocol = 'https:';
  url.hash = '';
  if (url.hostname === 'github.com') {
    const file = url.pathname.match(/^\/([^/]+)\/([^/]+)\/(?:blob|raw)\/(.+)$/);
    if (file) {
      return {
        ok: true,
        url: `https://raw.githubusercontent.com/${file[1]}/${file[2]}/${file[3]}`,
      };
    }
  }
  return { ok: true, url: url.href };
}

/** A tunebook that does not name itself takes its file's name, as an upload does. */
export function abcBookNameFromAddress(address: string): string | undefined {
  let url: URL;
  try {
    url = new URL(address);
  } catch {
    return undefined;
  }
  if (url.protocol === 'data:') return undefined;
  const last = url.pathname.split('/').filter(Boolean).pop();
  if (!last) return undefined;
  let name = last;
  try {
    name = decodeURIComponent(last);
  } catch {
    /* keep it as written */
  }
  const clean = name
    .replace(/\.(?:abct?|txt)$/i, '')
    .replace(/[-_]+/g, ' ')
    .trim();
  return clean || undefined;
}

/** "raw.githubusercontent.com", or a stand-in for an address with no host. */
export function abcLinkHost(address: string): string {
  try {
    return new URL(address).host || 'that address';
  } catch {
    return 'that address';
  }
}

/**
 * The file's text. ABC 2.1 is UTF-8, but older tunebooks are Latin-1, which is
 * not valid UTF-8 once it carries an accent; read as UTF-8 it would show
 * replacement marks, so bytes UTF-8 cannot read are read as Windows-1252 (a
 * superset of Latin-1). A UTF-16 byte order mark is honoured.
 */
export function decodeAbcBytes(bytes: Uint8Array): string {
  if (bytes[0] === 0xff && bytes[1] === 0xfe) return new TextDecoder('utf-16le').decode(bytes);
  if (bytes[0] === 0xfe && bytes[1] === 0xff) return new TextDecoder('utf-16be').decode(bytes);
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder('windows-1252').decode(bytes);
  }
}

/** The body, or null once it runs past `maxBytes` (reading stops there). */
async function readCapped(res: Response, maxBytes: number): Promise<Uint8Array | null> {
  const reader = res.body?.getReader();
  if (!reader) {
    const whole = new Uint8Array(await res.arrayBuffer());
    return whole.byteLength > maxBytes ? null : whole;
  }
  const parts: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    total += value.byteLength;
    if (total > maxBytes) {
      void reader.cancel().catch(() => undefined);
      return null;
    }
    parts.push(value);
  }
  const out = new Uint8Array(total);
  let at = 0;
  for (const part of parts) {
    out.set(part, at);
    at += part.byteLength;
  }
  return out;
}

export interface AbcFetchOptions {
  fetchImpl?: typeof fetch;
  /** Abort from outside (the link was left before it arrived). */
  signal?: AbortSignal;
  maxBytes?: number;
  timeoutMs?: number;
}

export type AbcFetchResult = { ok: true; text: string } | { ok: false; error: string };

/** Fetch the ABC at an address, with every failure as a sentence for a reader. */
export async function fetchAbcText(
  address: string,
  opts: AbcFetchOptions = {},
): Promise<AbcFetchResult> {
  const fetchImpl = opts.fetchImpl ?? ((input, init) => fetch(input, init));
  const maxBytes = opts.maxBytes ?? ABC_LINK_MAX_BYTES;
  const host = abcLinkHost(address);
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, opts.timeoutMs ?? ABC_LINK_TIMEOUT_MS);
  const stop = () => controller.abort();
  if (opts.signal?.aborted) controller.abort();
  opts.signal?.addEventListener('abort', stop, { once: true });
  const unreachable = (): AbcFetchResult => ({
    ok: false,
    error: timedOut ? `${host} took too long to answer.` : `Couldn't load the ABC from ${host}.`,
  });
  const tooBig: AbcFetchResult = { ok: false, error: 'That ABC file is too big to open.' };
  try {
    let res: Response;
    try {
      res = await fetchImpl(address, {
        credentials: 'omit',
        redirect: 'follow',
        signal: controller.signal,
      });
    } catch {
      return unreachable();
    }
    if (!res.ok) {
      return res.status === 404 || res.status === 410
        ? { ok: false, error: `${host} has no file at that address.` }
        : unreachable();
    }
    const declared = Number(res.headers.get('content-length') ?? NaN);
    if (Number.isFinite(declared) && declared > maxBytes) return tooBig;
    let bytes: Uint8Array | null;
    try {
      bytes = await readCapped(res, maxBytes);
    } catch {
      return unreachable();
    }
    if (!bytes) return tooBig;
    const text = decodeAbcBytes(bytes);
    if (!isAbcSource(text)) return { ok: false, error: "That address isn't an ABC file." };
    return { ok: true, text };
  } finally {
    clearTimeout(timer);
    opts.signal?.removeEventListener('abort', stop);
  }
}

export interface AbcLinkNotice {
  text: string;
  tone: 'info' | 'error';
}

/** Where to go once the link has been read, and what to say about it. */
export type AbcLinkOutcome =
  | { status: 'opened'; to: string; notice?: AbcLinkNotice }
  | { status: 'failed'; to: string; notice: AbcLinkNotice }
  /** Another tune arrived while the file was on its way: the reader moved on. */
  | { status: 'stale'; to: string };

/** The notice shown while the file is on its way. */
export function abcLinkOpeningText(raw: string, page: { protocol: string }): string {
  const address = abcLinkAddress(raw, page);
  if (!address.ok) return 'Opening ABC…';
  const host = abcLinkHost(address.url);
  return host === 'that address' ? 'Opening ABC…' : `Opening ABC from ${host}…`;
}

function withoutLinkParams(pathname: string, params: URLSearchParams): string {
  const next = new URLSearchParams(params);
  for (const key of LINK_KEYS) next.delete(key);
  const search = next.toString();
  return search ? `${pathname}?${search}` : pathname;
}

/**
 * Read an ABC link: fetch the file and open it in the store (headless, the
 * command layer's pattern), then say where to go. The caller navigates there,
 * replacing the link in the history, so Back and a reload never import twice.
 */
export async function openAbcLink(
  place: { pathname: string; search: string; protocol: string },
  opts: AbcFetchOptions = {},
): Promise<AbcLinkOutcome> {
  const params = new URLSearchParams(place.search);
  const stay = withoutLinkParams(place.pathname, params);
  const failed = (text: string): AbcLinkOutcome => ({
    status: 'failed',
    to: stay,
    notice: { text, tone: 'error' },
  });
  const address = abcLinkAddress(params.get(ABC_LINK_PARAM) ?? '', place);
  if (!address.ok) return failed(address.error);

  const startSeq = useStore.getState().tuneLoadSeq;
  const fetched = await fetchAbcText(address.url, opts);
  if (opts.signal?.aborted || useStore.getState().tuneLoadSeq !== startSeq) {
    return { status: 'stale', to: stay };
  }
  if (!fetched.ok) return failed(fetched.error);

  let index: IRealIndex;
  try {
    index = parseTuneIndex(fetched.text);
  } catch {
    return failed("That address isn't an ABC file.");
  }
  if (!isAbcScheme(index.scheme)) return failed("That address isn't an ABC file.");
  if (index.songs.length === 0) return failed('No tunes found in that ABC file.');

  const tuneRoute =
    place.pathname === APP_ROUTES.scoreStudio ? APP_ROUTES.scoreStudio : APP_ROUTES.workbench;
  const read = params.get('read');
  const applyRead = () => {
    if (isScoreEdition(read)) useStore.getState().setScoreEdition(read);
  };
  /** Open one tune the way a browser row does, then the link's reading and recipe. */
  const land = (song: PlaylistEntry, scheme: string): boolean => {
    const store = useStore.getState();
    const title = store.loadTuneFromChunk(scheme, song.chunk);
    if (title === null) return false;
    store.noteCurrentTuneRef({
      kind: 'ireal',
      title: song.title,
      composer: song.composer ?? '',
      scheme,
      chunk: song.chunk,
    });
    applyRead();
    const recipe = parseTuneRecipe(params);
    if (hasRecipe(recipe)) applyTuneRecipe(recipe);
    return true;
  };

  if (index.songs.length === 1) {
    return land(index.songs[0], index.scheme)
      ? { status: 'opened', to: tuneRoute }
      : failed("Couldn't read that tune.");
  }

  const res = useStore
    .getState()
    .setPlaylist(withAbcBookName(fetched.text, abcBookNameFromAddress(address.url)));
  if (!res.ok) return failed(res.error);
  const book = useStore.getState().playlist;
  if (!book) return failed('No tunes found in that ABC file.');
  const name = book.name?.trim() || 'the tunebook';
  const opened: AbcLinkNotice = {
    text: `Opened ${name} · ${book.songs.length} tunes${res.persisted ? '' : ' · too big to keep after a reload'}`,
    tone: 'info',
  };
  const wanted = params.get('tune')?.trim();
  if (wanted) {
    const title = wanted.toLowerCase();
    const by = params.get('by')?.trim().toLowerCase();
    const matches = book.songs.filter((song) => song.title.trim().toLowerCase() === title);
    const song =
      matches.find((candidate) => (candidate.composer ?? '').trim().toLowerCase() === by) ??
      matches[0];
    if (song && land(song, book.scheme)) return { status: 'opened', to: tuneRoute, notice: opened };
    applyRead();
    return {
      status: 'opened',
      to: ABC_BOOK_LIST,
      notice: {
        text: song ? "Couldn't read that tune." : `No tune called “${wanted}” in ${name}.`,
        tone: 'error',
      },
    };
  }
  applyRead();
  return { status: 'opened', to: ABC_BOOK_LIST, notice: opened };
}
